<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import type {
  FormGroup,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type { IProfileSettingsChangeEmailSchema } from '@modules/profile-settings/submodules/change-email/types';
import VLoader from '@components/loader/VLoader.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import {
  PROFILE_SETTINGS_PERMISSIONS,
  TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS,
  TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS,
} from '@modules/profile-settings/constants';
import {
  useAdminProfileQuery,
  useAffiliateProfileQuery,
} from '@modules/profile-settings/queries';
import { useProfileSettingsChangeEmailMutation } from '@modules/profile-settings/submodules/change-email/mutations';
import {
  ADMIN_PROFILE_SETTINGS_CONFIRMATION_EMAIL_ROUTE_NAME,
  AFFILIATE_PROFILE_SETTINGS_CONFIRMATION_EMAIL_ROUTE_NAME,
} from '@modules/profile-settings/submodules/confirmation-email/constants';
import { useUserSupervision } from '@modules/user/composables';

const { isAdminNamespace } = useCurrentNamespace();
const { isSupervision } = useUserSupervision();
const { getTranslation } = useI18n();
const router = useRouter();

const { data: profileAdmin, isLoading: isLoadingAdminData, refetch: refetchAdmin } = useAdminProfileQuery();
const { data: profileAffiliate, isLoading: isLoadingAffiliate, refetch: refetchAffiliate } = useAffiliateProfileQuery();
const {
  mutateAsync,
  isLoading: isLoadingSave,
  errors,
  clearError,
} = useProfileSettingsChangeEmailMutation();

const translationGroup = computed(() => (
  isAdminNamespace.value ? TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS : TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS
));

const profile = computed(() => (
  isAdminNamespace.value ? profileAdmin.value : profileAffiliate.value
));

const isLoading = computed(() => (
  isAdminNamespace.value ? isLoadingAdminData.value : isLoadingAffiliate.value
));

const schema = computed<FormSchema<IProfileSettingsChangeEmailSchema>>(() => {
  const responseSchema: FormSchema<IProfileSettingsChangeEmailSchema> = {
    email: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(translationGroup.value, 'EMAIL_CURRENT_EMAIL').value,
      value: profile.value?.email,
      required: true,
      autocomplete: 'username',
    },
    current_password: {
      type: EFormInputTypes.PASSWORD,
      label: getTranslation(translationGroup.value, 'EMAIL_CURRENT_PASSWORD').value,
      required: true,
      disabled: isSupervision.value,
      autocomplete: 'current-password',
    },
  };

  if (profile.value?.unconfirmed_email) {
    responseSchema.unconfirmed_email = {
      type: EFormInputTypes.TEXT,
      label: getTranslation(translationGroup.value, 'EMAIL_UNCONFIRMED_EMAIL').value,
      value: profile.value.unconfirmed_email,
      required: true,
    };
  }

  return responseSchema;
});

const formGroups = computed<FormGroup<IProfileSettingsChangeEmailSchema>[]>(() => [{
  items: ['email', 'unconfirmed_email', 'current_password'],
}]);

async function onSubmit(profile: IProfileSettingsChangeEmailSchema): Promise<void> {
  await mutateAsync({ profile });

  if (isAdminNamespace.value) {
    const response = await refetchAdmin();
    if (response.data?.unconfirmed_email) {
      void router.push({
        name: ADMIN_PROFILE_SETTINGS_CONFIRMATION_EMAIL_ROUTE_NAME,
      });
    }
  } else {
    const response = await refetchAffiliate();
    if (response.data?.unconfirmed_email) {
      void router.push({
        name: AFFILIATE_PROFILE_SETTINGS_CONFIRMATION_EMAIL_ROUTE_NAME,
      });
    }
  }
}
</script>

<template>
  <VLoader v-data-qa-auto-id="'ChangeEmailPage'"
    :loading="isLoading"
    min-height="200px"
  >
    <VForm
      :schema="schema"
      :groups="formGroups"
      :readonly="!$hasPermission(PROFILE_SETTINGS_PERMISSIONS.EDIT) || isSupervision"
      :submit-btn-label="$getTranslation(translationGroup, 'SAVE')"
      :custom-errors="errors"
      :is-loading="isLoadingSave"
      @interacted="clearError"
      @submit="onSubmit"
    />
  </VLoader>
</template>
